import { useNavigate } from 'react-router-dom'
import { CHART_COLORS } from '@/lib/chart-theme'

/* ── Mini chart SVG components for card previews ── */

function MiniFunnel({ steps }: { steps: { pct: number; color: string }[] }) {
  const h = 80
  const barH = 14
  const gap = 4
  const totalH = steps.length * (barH + gap) - gap
  const offsetY = (h - totalH) / 2
  return (
    <svg width="100%" height={h} viewBox="0 0 200 80" className="rep-mini-svg">
      {steps.map((s, i) => {
        const w = 40 + (s.pct / 100) * 140
        const x = (200 - w) / 2
        return (
          <rect key={i} x={x} y={offsetY + i * (barH + gap)} width={w} height={barH}
            rx={4} ry={4} fill={s.color} />
        )
      })}
    </svg>
  )
}

function MiniTreemap({ slices }: { slices: { pct: number; color: string }[] }) {
  // Simple proportional blocks in a row
  const h = 80
  const total = slices.reduce((s, sl) => s + sl.pct, 0) || 1
  let x = 10
  const gap = 3
  const usableW = 180
  return (
    <svg width="100%" height={h} viewBox="0 0 200 80" className="rep-mini-svg">
      {slices.map((sl, i) => {
        const w = Math.max((sl.pct / total) * usableW - gap, 12)
        const rect = (
          <rect key={i} x={x} y={10} width={w} height={60} rx={6} ry={6} fill={sl.color} />
        )
        x += w + gap
        return rect
      })}
    </svg>
  )
}

function MiniDonut({ slices }: { slices: { pct: number; color: string }[] }) {
  const size = 110
  const cx = 100
  const cy = 55
  const r = 42
  const innerR = 24
  let startAngle = -90
  return (
    <svg width="100%" height={size} viewBox="0 0 200 110" className="rep-mini-svg">
      {slices.map((s, i) => {
        const angle = (s.pct / 100) * 360
        const endAngle = startAngle + angle
        const largeArc = angle > 180 ? 1 : 0
        const x1 = cx + r * Math.cos((startAngle * Math.PI) / 180)
        const y1 = cy + r * Math.sin((startAngle * Math.PI) / 180)
        const x2 = cx + r * Math.cos((endAngle * Math.PI) / 180)
        const y2 = cy + r * Math.sin((endAngle * Math.PI) / 180)
        const ix1 = cx + innerR * Math.cos((endAngle * Math.PI) / 180)
        const iy1 = cy + innerR * Math.sin((endAngle * Math.PI) / 180)
        const ix2 = cx + innerR * Math.cos((startAngle * Math.PI) / 180)
        const iy2 = cy + innerR * Math.sin((startAngle * Math.PI) / 180)
        const d = `M${x1},${y1} A${r},${r} 0 ${largeArc} 1 ${x2},${y2} L${ix1},${iy1} A${innerR},${innerR} 0 ${largeArc} 0 ${ix2},${iy2} Z`
        startAngle = endAngle
        return <path key={i} d={d} fill={s.color} />
      })}
    </svg>
  )
}

function MiniAreaRange({ color, avg, high, low }: { color: string; avg: number[]; high: number[]; low: number[] }) {
  const w = 200
  const h = 80
  const allVals = [...high, ...low]
  const maxV = Math.max(...allVals, 1)
  const minV = Math.min(...allVals, 0)
  const range = maxV - minV || 1
  const step = w / (avg.length - 1)
  const yScale = (v: number) => 8 + (1 - (v - minV) / range) * (h - 16)

  let bandPath = `M0,${yScale(high[0])}`
  high.forEach((v, i) => { if (i) bandPath += ` L${i * step},${yScale(v)}` })
  for (let i = low.length - 1; i >= 0; i--) bandPath += ` L${i * step},${yScale(low[i])}`
  bandPath += ' Z'

  const avgPath = avg.map((v, i) => `${i ? 'L' : 'M'}${i * step},${yScale(v)}`).join(' ')

  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} className="rep-mini-svg" preserveAspectRatio="none">
      <path d={bandPath} fill={color} opacity={0.12} />
      <path d={avgPath} fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
      {avg.map((v, i) => (
        <circle key={i} cx={i * step} cy={yScale(v)} r={2.5} fill={color} />
      ))}
    </svg>
  )
}

function MiniRadar({ colors }: { colors: string[] }) {
  const cx = 100, cy = 55, r = 44
  const axes = 5
  // Draw 3 polygons for 3 estimators
  const shapes = [
    [0.9, 0.6, 0.7, 0.5, 0.8],
    [0.5, 0.9, 0.6, 0.8, 0.4],
    [0.7, 0.5, 0.9, 0.7, 0.6],
  ]
  return (
    <svg width="100%" height={110} viewBox="0 0 200 110" className="rep-mini-svg">
      {/* Grid rings */}
      {[0.33, 0.66, 1].map((s, i) => (
        <polygon key={i} fill="none" stroke="#E2E8F0" strokeWidth={0.5}
          points={Array.from({ length: axes }).map((_, j) => {
            const angle = (j * 360 / axes - 90) * Math.PI / 180
            return `${cx + r * s * Math.cos(angle)},${cy + r * s * Math.sin(angle)}`
          }).join(' ')} />
      ))}
      {/* Data shapes */}
      {shapes.map((vals, si) => (
        <polygon key={si} fill={colors[si % colors.length]} fillOpacity={0.15}
          stroke={colors[si % colors.length]} strokeWidth={1.5}
          points={vals.map((v, j) => {
            const angle = (j * 360 / axes - 90) * Math.PI / 180
            return `${cx + r * v * Math.cos(angle)},${cy + r * v * Math.sin(angle)}`
          }).join(' ')} />
      ))}
    </svg>
  )
}

function MiniScatter({ colors }: { colors: string[] }) {
  const dots = [
    { x: 30, y: 55, s: 14 },
    { x: 70, y: 30, s: 10 },
    { x: 120, y: 45, s: 18 },
    { x: 160, y: 20, s: 12 },
  ]
  return (
    <svg width="100%" height={80} viewBox="0 0 200 80" className="rep-mini-svg">
      {/* Reference line */}
      <line x1="95" y1="0" x2="95" y2="80" stroke={CHART_COLORS.coral} strokeDasharray="4 3" strokeWidth={1} opacity={0.6} />
      {dots.map((d, i) => (
        <circle key={i} cx={d.x} cy={d.y} r={d.s} fill={colors[i % colors.length]} opacity={0.7} />
      ))}
    </svg>
  )
}

function MiniDonutRings({ items }: { items: { pct: number; color: string }[] }) {
  const ringR = 18, strokeW = 5, gap = 48
  const totalW = items.length * gap
  const offsetX = (200 - totalW) / 2 + gap / 2
  return (
    <svg width="100%" height={110} viewBox="0 0 200 110" className="rep-mini-svg">
      {items.map((item, i) => {
        const cx = offsetX + i * gap
        const cy = 55
        const circ = 2 * Math.PI * ringR
        const dashOffset = circ * (1 - item.pct / 100)
        return (
          <g key={i}>
            <circle cx={cx} cy={cy} r={ringR} fill="none" stroke="#E2E8F0" strokeWidth={strokeW} />
            <circle cx={cx} cy={cy} r={ringR} fill="none" stroke={item.color} strokeWidth={strokeW}
              strokeDasharray={circ} strokeDashoffset={dashOffset} strokeLinecap="round"
              transform={`rotate(-90 ${cx} ${cy})`} />
          </g>
        )
      })}
    </svg>
  )
}

function MiniTimeline({ segments }: { segments: { pct: number; color: string }[] }) {
  const y = 32, h = 16, total = segments.reduce((s, sg) => s + sg.pct, 0) || 1
  let x = 15
  const usableW = 170
  return (
    <svg width="100%" height={80} viewBox="0 0 200 80" className="rep-mini-svg">
      {segments.map((sg, i) => {
        const w = Math.max((sg.pct / total) * usableW, 20)
        const rect = (
          <rect key={i} x={x} y={y} width={w} height={h} rx={i === 0 ? 6 : 0}
            ry={i === 0 ? 6 : 0} fill={sg.color}
            style={i === segments.length - 1 ? { borderRadius: '0 6px 6px 0' } : undefined} />
        )
        x += w
        return rect
      })}
      {/* Round right end */}
      <rect x={x - 1} y={y} width={2} height={h} rx={6} fill={segments[segments.length - 1]?.color || '#ccc'} />
    </svg>
  )
}

function MiniHeatGrid({ cells }: { cells: { intensity: number; color: string }[] }) {
  const cols = 5, size = 28, gap = 4
  const offsetX = (200 - cols * (size + gap) + gap) / 2
  const offsetY = (80 - 2 * (size + gap) + gap) / 2
  return (
    <svg width="100%" height={80} viewBox="0 0 200 80" className="rep-mini-svg">
      {cells.map((c, i) => {
        const col = i % cols
        const row = Math.floor(i / cols)
        return (
          <rect key={i} x={offsetX + col * (size + gap)} y={offsetY + row * (size + gap)}
            width={size} height={size} rx={4} ry={4}
            fill={c.color} opacity={0.15 + c.intensity * 0.7} />
        )
      })}
    </svg>
  )
}

function MiniDualLine({ color1, color2, points1, points2 }: { color1: string; color2: string; points1: number[]; points2: number[] }) {
  const w = 200, h = 80
  const all = [...points1, ...points2]
  const maxV = Math.max(...all, 1)
  const step = w / (points1.length - 1)
  const toY = (v: number) => h - 8 - (v / maxV) * (h - 16)

  const path1 = points1.map((v, i) => `${i ? 'L' : 'M'}${i * step},${toY(v)}`).join(' ')
  const path2 = points2.map((v, i) => `${i ? 'L' : 'M'}${i * step},${toY(v)}`).join(' ')

  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} className="rep-mini-svg" preserveAspectRatio="none">
      <path d={path1} fill="none" stroke={color1} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
      <path d={path2} fill="none" stroke={color2} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" strokeDasharray="6 3" />
      {points1.map((v, i) => <circle key={`a${i}`} cx={i * step} cy={toY(v)} r={2.5} fill={color1} />)}
      {points2.map((v, i) => <circle key={`b${i}`} cx={i * step} cy={toY(v)} r={2.5} fill={color2} />)}
    </svg>
  )
}

function MiniHBars({ items }: { items: { pct: number; color: string }[] }) {
  const h = 80
  const barH = 12
  const gap = 6
  const totalH = items.length * (barH + gap) - gap
  const offsetY = (h - totalH) / 2
  return (
    <svg width="100%" height={h} viewBox="0 0 200 80" className="rep-mini-svg">
      {items.map((item, i) => (
        <rect
          key={i}
          x={10}
          y={offsetY + i * (barH + gap)}
          width={Math.max(20, (item.pct / 100) * 180)}
          height={barH}
          rx={4}
          ry={4}
          fill={item.color}
        />
      ))}
    </svg>
  )
}

function MiniBarStack({ bars }: { bars: { heights: number[]; color: string }[] }) {
  const h = 80
  const barW = 20
  const gap = 8
  const count = bars[0]?.heights.length || 0
  const totalW = count * (barW + gap) - gap
  const offsetX = (200 - totalW) / 2
  const maxH = Math.max(...bars.flatMap((b) => b.heights), 1)
  return (
    <svg width="100%" height={h} viewBox="0 0 200 80" className="rep-mini-svg">
      {bars[0]?.heights.map((val, i) => {
        const barH = (val / maxH) * (h - 16)
        return (
          <rect key={i} x={offsetX + i * (barW + gap)} y={h - 8 - barH} width={barW} height={barH}
            rx={3} ry={3} fill={bars[0].color} opacity={0.6 + (val / maxH) * 0.4} />
        )
      })}
    </svg>
  )
}

/* ── Report definitions ── */

interface ReportDef {
  id: string
  title: string
  description: string
  chart: React.ReactNode
}

const { blue, purple, magenta, coral } = CHART_COLORS

const salesReports: ReportDef[] = [
  {
    id: 'win-loss',
    title: 'Win / Loss',
    description: 'Proposal funnel from sent to signed',
    chart: (
      <MiniFunnel steps={[
        { pct: 100, color: blue },
        { pct: 78, color: purple },
        { pct: 55, color: magenta },
        { pct: 38, color: coral },
      ]} />
    ),
  },
  {
    id: 'pipeline',
    title: 'Pipeline',
    description: 'Current estimate & proposal value by stage',
    chart: (
      <MiniTreemap slices={[
        { pct: 45, color: blue },
        { pct: 30, color: purple },
        { pct: 15, color: magenta },
        { pct: 10, color: coral },
      ]} />
    ),
  },
  {
    id: 'revenue-by-type',
    title: 'Revenue by Type',
    description: 'Signed revenue by project type',
    chart: (
      <MiniDonut slices={[
        { pct: 40, color: blue },
        { pct: 25, color: purple },
        { pct: 20, color: magenta },
        { pct: 15, color: coral },
      ]} />
    ),
  },
  {
    id: 'avg-deal-size',
    title: 'Average Deal Size',
    description: 'Bid total trends with high/low range',
    chart: <MiniAreaRange color={blue}
      avg={[45, 52, 48, 60, 55, 68, 62, 72]}
      high={[65, 75, 68, 82, 78, 90, 85, 95]}
      low={[28, 35, 30, 40, 35, 48, 42, 50]}
    />,
  },
  {
    id: 'revenue-collected',
    title: 'Revenue Collected',
    description: 'Cash received by month, method & client',
    chart: (
      <MiniBarStack bars={[
        { heights: [60, 45, 70, 55, 80, 65], color: blue },
      ]} />
    ),
  },
]

const performanceReports: ReportDef[] = [
  {
    id: 'estimator-comparison',
    title: 'Estimator Comparison',
    description: 'Multi-dimensional performance radar',
    chart: <MiniRadar colors={[blue, purple, magenta]} />,
  },
  {
    id: 'markup-analysis',
    title: 'Markup Analysis',
    description: 'Markup vs. value scatter with company average',
    chart: <MiniScatter colors={[blue, purple, magenta, coral]} />,
  },
  {
    id: 'rejection-rate',
    title: 'Rejection Rate',
    description: 'Per-estimator rejection donut rings',
    chart: (
      <MiniDonutRings items={[
        { pct: 15, color: blue },
        { pct: 35, color: purple },
        { pct: 8, color: magenta },
        { pct: 25, color: coral },
      ]} />
    ),
  },
]

const efficiencyReports: ReportDef[] = [
  {
    id: 'cycle-time',
    title: 'Estimate Cycle Time',
    description: 'Workflow swim-lane timeline',
    chart: (
      <MiniTimeline segments={[
        { pct: 45, color: blue },
        { pct: 30, color: purple },
        { pct: 25, color: magenta },
      ]} />
    ),
  },
  {
    id: 'approval-queue',
    title: 'Approval Queue',
    description: 'Wait-time heatmap for pending estimates',
    chart: (
      <MiniHeatGrid cells={[
        { intensity: 0.3, color: magenta },
        { intensity: 0.8, color: magenta },
        { intensity: 0.2, color: magenta },
        { intensity: 0.6, color: magenta },
        { intensity: 0.9, color: magenta },
        { intensity: 0.5, color: magenta },
        { intensity: 0.1, color: magenta },
        { intensity: 0.7, color: magenta },
        { intensity: 0.4, color: magenta },
        { intensity: 0.6, color: magenta },
      ]} />
    ),
  },
  {
    id: 'opportunity-queue',
    title: 'Opportunity Queue',
    description: 'Outstanding opportunities by estimator with bid-due countdown',
    chart: (
      <MiniHBars items={[
        { pct: 80, color: blue },
        { pct: 60, color: purple },
        { pct: 45, color: magenta },
        { pct: 25, color: coral },
      ]} />
    ),
  },
  {
    id: 'response-time',
    title: 'Proposal Response Time',
    description: 'View vs. sign time dual trend lines',
    chart: <MiniDualLine color1={blue} color2={purple}
      points1={[50, 35, 45, 30, 25, 35, 20, 28]}
      points2={[70, 55, 65, 50, 45, 55, 40, 48]}
    />,
  },
]

/* ── Card Component ── */

function ReportCard({ report }: { report: ReportDef }) {
  const navigate = useNavigate()
  return (
    <div className="rep-card" onClick={() => navigate(`/reports/${report.id}`)}>
      <div className="rep-card-preview">{report.chart}</div>
      <div className="rep-card-body">
        <h3 className="rep-card-title">{report.title}</h3>
        <p className="rep-card-desc">{report.description}</p>
      </div>
      <div className="rep-card-footer">
        <span className="rep-card-link">View Report →</span>
      </div>
    </div>
  )
}

/* ── Section Component ── */

function ReportSection({ title, icon, reports }: { title: string; icon: React.ReactNode; reports: ReportDef[] }) {
  return (
    <div className="rep-hub-section">
      <div className="rep-hub-section-header">
        {icon}
        <h2 className="rep-hub-section-title">{title}</h2>
      </div>
      <div className="rep-hub-grid">
        {reports.map((r) => (
          <ReportCard key={r.id} report={r} />
        ))}
      </div>
    </div>
  )
}

/* ── Inline SVG Icons ── */
const SalesIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
)
const PerformanceIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" />
  </svg>
)
const EfficiencyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
)

/* ── Main Hub Page ── */

export function ReportsHubPage() {
  return (
    <div className="rep-hub">
      <div className="rep-hub-header">
        <h1 className="rep-hub-page-title">Reports</h1>
        <p className="rep-hub-page-subtitle">Click any report to explore with custom filters and export data.</p>
      </div>

      <ReportSection title="Sales" icon={<SalesIcon />} reports={salesReports} />
      <ReportSection title="Estimator Performance" icon={<PerformanceIcon />} reports={performanceReports} />
      <ReportSection title="Operational Efficiency" icon={<EfficiencyIcon />} reports={efficiencyReports} />
    </div>
  )
}
